import { useMemo } from "react";
import {
  MEMBER_PROJECTS_TABLE_COLUMN_LABELS,
  MemberProjectsTableColumnField,
  MemberProjectsTableGridColDef,
} from "@pages/member-profile/tab-contents/member-project-tab/member-projects-table-types";
import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";
import {
  ProjectArchivedStateCell,
  ProjectCreatedAtCell,
  ProjectIdCell,
  ProjectMemberRoleCell,
  ProjectModifiedAtCell,
  ProjectNameCell,
  ProjectOptionsCell,
} from "@utils/data-grid-utils/projects-data-grid-cells";

const columnDimension: ColumnDimension<MemberProjectsTableColumnField> = {
  name: {
    flex: 1.5,
  },
  id: {
    flex: 1.5,
  },
  role: {
    flex: 1,
  },
  archived: {
    flex: 0.5,
  },
  createdAt: {
    flex: 1,
  },
  modifiedAt: {
    flex: 1,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the member's projects table
 */
export function useMemberProjectsTableColumns(): MemberProjectsTableGridColDef[] {
  const columnsMap: Record<
    MemberProjectsTableColumnField,
    MemberProjectsTableGridColDef
  > = useMemo(() => {
    return {
      name: {
        field: "name",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.name,
        ...columnDimension.name,
        valueGetter: (_, row) => row.entity.name,
        renderCell: ProjectNameCell,
      },

      id: {
        field: "id",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.id,
        ...columnDimension.id,
        valueGetter: (_, row) => row.entity.projectId,
        renderCell: ProjectIdCell,
      },

      role: {
        field: "role",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.role,
        ...columnDimension.role,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.projectRole,
        renderCell: ProjectMemberRoleCell,
      },

      archived: {
        field: "archived",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.archived,
        ...columnDimension.archived,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.archived,
        renderCell: ProjectArchivedStateCell,
      },

      createdAt: {
        field: "createdAt",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.createdAt,
        ...columnDimension.createdAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.createdAt,
        renderCell: ProjectCreatedAtCell,
      },

      modifiedAt: {
        field: "modifiedAt",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.modifiedAt,
        ...columnDimension.modifiedAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.modifiedAt,
        renderCell: ProjectModifiedAtCell,
      },

      options: {
        field: "options",
        type: "actions",
        headerName: MEMBER_PROJECTS_TABLE_COLUMN_LABELS.options,
        ...columnDimension.options,
        getApplyQuickFilterFn: () => null,
        align: "right",
        renderCell: (params) =>
          ProjectOptionsCell(params, "member-projects-table"),
      },
    };
  }, []);

  return Object.values(columnsMap);
}
